import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';

import App from 'App';
import API from 'API';
import TextHelpers from 'helpers/TextHelpers';

// Components
import Loader from 'components/common/Loader';
import FindProperty from 'components/steps/FindProperty';
import NoAccount from 'components/steps/NoAccount';
import PersonalDetails from 'components/steps/PersonalDetails';
import ChimneyDetails from 'components/steps/ChimneyDetails';
import ConfirmPrice from 'components/steps/ConfirmPrice';
import SelectDateAndTime from 'components/steps/SelectDateAndTime';
import Summary from 'components/steps/Summary';
import Thanks from 'components/steps/Thanks';

//-------------------------------------------------------------------------------------------------------------------

class BookAppt extends Component {
    
    constructor(props) {
        super(props);

        this.tryLoadAccountInfo = this.tryLoadAccountInfo.bind(this);
        this.goToStep = this.goToStep.bind(this);

        let bookingRequest = this.loadFromLocalStorage();
        bookingRequest ||= {
            accountID: null,
            houseNumber: null,
            postcode: null,
            customerFirstName: null,
            customerLastName: null,
            customerEmail: null,
            customerTel: null,
            version: App.VERSION
        };
        if (!bookingRequest.sessionID) {
            bookingRequest.sessionID = TextHelpers.generateRandomString(16);
            this.saveToLocalStorage(bookingRequest);
        }

        this.state = {
            bookingRequest,
            isLoading: true
        };
    }

    componentDidMount() {
        this.tryLoadAccountInfo();
    }

    componentDidUpdate(oldProps) {
        if (this.props.stepName != oldProps.stepName) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    goToStep(stepName) {
        this.props.history.push(`/${stepName}`);
    }

    updateFields(newValues, callback) {
        const bookingRequest = { ...this.state.bookingRequest };
        for (let fieldName in newValues) {
            const newValue = newValues[fieldName];
            bookingRequest[fieldName] = newValue;

            if (fieldName == 'customerFirstName' || fieldName == 'customerLastName') {
                bookingRequest.billingName = `${bookingRequest.customerFirstName} ${bookingRequest.customerLastName}`;
            }
            else if (fieldName == 'postcode') {
                bookingRequest.billingPostcode = bookingRequest.postcode;
            }
        }
        this.debounceSaveToLocalStorage();
        this.setState({ bookingRequest }, callback);
    }

    debounceSaveToLocalStorage() {
        clearTimeout(this.saveToLocalStorageTimeout);
        this.saveToLocalStorageTimeout = setTimeout(() => {
            this.saveToLocalStorage(this.state.bookingRequest);
        }, 250);
    }
    
    saveToLocalStorage(bookingRequest) {
        try {
            const json = JSON.stringify(bookingRequest);
            localStorage.setItem('appt', json);
        } catch (error) {
            console.log(error);
        }
    }
    
    loadFromLocalStorage() {
        try {
            const json = localStorage.getItem('appt');
            const bookingRequest = JSON.parse(json);
            return bookingRequest;
        } catch (error) {
            console.log(error);
        }
    }

    async tryLoadAccountInfo() {
        return new Promise(async (resolve) => {
            let { bookingRequest } = this.state;
            let accountInfo = null;

            // Load
            if (bookingRequest.postcode) {
                accountInfo = await API.call('book/get-account-info', bookingRequest);
            }
            this.setState({ accountInfo, isLoading: false });

            // Update globally
            this.props.fns.setAccountInfo(accountInfo);
            
            // If not found, go to error page
            if (!accountInfo && bookingRequest && bookingRequest.postcode && this.props.stepName != 'findProperty') {
                this.goToStep('findProperty');
            }
            
            // Return found true/false
            resolve(!!accountInfo);
        });
    }

    startAgain(callback) {
        const bookingRequest = {
            version: App.VERSION
        };
        this.saveToLocalStorage(bookingRequest);
        this.setState({ bookingRequest }, callback);
    }

    render() {
        const { isLoading } = this.state;
        const { stepName } = this.props;
        if (isLoading) {
            return (<Loader />);
        }
        return this.renderStep(stepName);
    }

    renderStep(stepName) {
        const { bookingRequest, accountInfo } = this.state;
        const subProps = {
            ...this.props,
            fns: {
                ...this.props.fns,
                tryLoadAccountInfo: this.tryLoadAccountInfo
            },
            bookingRequest,
            accountInfo
        };

        switch (stepName) {
            case 'findProperty':
                return (
                    <FindProperty
                        {...subProps}
                        onChange={(newValues) => this.updateFields(newValues)}
                        onNextClicked={() => {
                            this.goToStep('personalDetails');
                        }}
                    />
                );
            case 'noAccount':
                return (
                    <NoAccount
                        {...subProps}
                        onBackClicked={() => {
                            this.goToStep('findProperty');
                        }}
                    />
                );
            case 'personalDetails':
                return (
                    <PersonalDetails
                        {...subProps}
                        onChange={(newValues) => this.updateFields(newValues)}
                        onNextClicked={() => {
                            this.goToStep('chimneyDetails');
                        }}
                        onBackClicked={() => {
                            this.goToStep('findProperty');
                        }}
                    />
                );
            case 'chimneyDetails':
                return (
                    <ChimneyDetails
                        {...subProps}
                        onChange={(newValues, callback) => this.updateFields(newValues, callback)}
                        onChimneyTypeSelected={() => {
                            this.goToStep('confirmPrice');
                        }}
                        onBackClicked={() => {
                            this.goToStep('personalDetails');
                        }}
                    />
                );
            case 'confirmPrice':
                return (
                    <ConfirmPrice
                        {...subProps}
                        onChange={(newValues, callback) => this.updateFields(newValues, callback)}
                        onNextClicked={() => {
                            this.goToStep('selectDateAndTime');
                        }}
                        onBackClicked={() => {
                            this.goToStep('chimneyDetails');
                        }}
                    />
                );
            case 'selectDateAndTime':
                return (
                    <SelectDateAndTime
                        {...subProps}
                        onChange={(newValues, callback) => this.updateFields(newValues, callback)}
                        onNextClicked={() => {
                            this.goToStep('summary');
                        }}
                        onBackClicked={() => {
                            this.goToStep('confirmPrice');
                        }}
                    />
                );
            case 'summary':
                return (
                    <Summary
                        {...subProps}
                        onChange={(newValues, callback) => this.updateFields(newValues, callback)}
                        onNextClicked={() => {
                            this.startAgain(() => {
                                this.goToStep('thanks');
                            });
                        }}
                        onBackClicked={() => {
                            this.goToStep('selectDateAndTime');
                        }}
                    />
                );
            case 'thanks':
                return (
                    <Thanks
                        {...subProps}
                        onChange={(newValues, callback) => this.updateFields(newValues, callback)}
                        onStartAgainClicked={() => {
                            this.goToStep('findProperty');
                        }}
                    />
                );
                break;
        }
        return null;
    }
}

export default withRouter(BookAppt);